import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Carousel — horizontal scroll-snap track with prev/next controls (§7.1 AC:
 * "carousels keyboard-accessible"). Used by the Home "Your Adventure Starts Here" and
 * Highlights rows.
 *
 * Accessibility: the track is a labelled, focusable region the user can scroll with the
 * arrow keys; the prev/next buttons are real <button>s that scroll by roughly one card and
 * disable at each end. Each item is a `snap-start` flex child sized 3-up on desktop.
 */
export function Carousel({
  title,
  subtitle,
  titleClassName,
  children,
}: {
  title: string;
  subtitle?: string;
  /** Lets the caller colour the heading (e.g. Highlights uses accent). */
  titleClassName?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const syncEdges = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  }, []);

  const scrollByPage = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    // One "page" ≈ the first card's width incl. gap, so the row advances card-by-card.
    const first = el.firstElementChild as HTMLElement | null;
    const step = first ? first.offsetWidth + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h2 className={`text-2xl font-bold sm:text-3xl ${titleClassName ?? 'text-content'}`}>{title}</h2>
          {subtitle && <p className="mt-1 text-content-muted">{subtitle}</p>}
        </div>
        <div className="flex shrink-0 gap-2">
          <CarouselButton dir="prev" onClick={() => scrollByPage(-1)} disabled={atStart} label={t('carousel.prev')} />
          <CarouselButton dir="next" onClick={() => scrollByPage(1)} disabled={atEnd} label={t('carousel.next')} />
        </div>
      </div>

      <div
        ref={trackRef}
        onScroll={syncEdges}
        tabIndex={0}
        role="group"
        aria-label={title}
        className="flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {children}
      </div>
    </section>
  );
}

/** A single 3-up snap item wrapper — keeps card sizing consistent across carousels. */
export function CarouselItem({ children }: { children: ReactNode }) {
  return <div className="w-[82%] shrink-0 snap-start sm:w-[46%] lg:w-[31.5%]">{children}</div>;
}

function CarouselButton({
  dir,
  onClick,
  disabled,
  label,
}: {
  dir: 'prev' | 'next';
  onClick: () => void;
  disabled: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={`inline-flex h-11 w-11 items-center justify-center rounded-pill border border-line bg-panel text-content transition-colors hover:bg-content/5 disabled:opacity-30 ${
        dir === 'next' ? 'text-content' : 'text-content-muted'
      }`}
    >
      <span aria-hidden="true">{dir === 'prev' ? '‹' : '›'}</span>
    </button>
  );
}
